// === CRASH HANDLER ===
// Capture les erreurs JS non gérées (window.error + unhandledrejection) et
// délègue à un callback unique (typiquement : afficher l'overlay de crash et
// proposer un retour au menu). Logique pure, testable sans DOM ni Phaser.
//
// Garde-fous :
//   - Déduplication : 2 crashes avec la même signature en < 2s = 1 seul appel.
//   - Garde anti-boucle : si trop de crashes (> MAX_BURST en BURST_WINDOW_MS),
//     on stoppe d'invoquer le callback (l'overlay est déjà visible, inutile
//     de le bombarder + risque de cascade infinie si l'overlay lui-même crashe).
//   - Réentrance : pendant que onCrash() s'exécute, on ignore les erreurs
//     déclenchées par le callback lui-même.

const DEDUPE_WINDOW_MS = 2000;
const BURST_WINDOW_MS = 30_000;
const MAX_BURST = 5;
const SIGNATURE_LEN = 200;

/** Type d'origine d'un crash. */
export type CrashKind = 'error' | 'rejection';

/** Informations normalisées sur un crash. */
export interface CrashInfo {
  kind: CrashKind;
  /** Message court (1ère ligne de l'erreur). */
  message: string;
  /** Stack trace si disponible, sinon undefined. */
  stack?: string;
  /** Fichier source + ligne, format `file.js:42` (best effort). */
  source?: string;
  /** Timestamp ms epoch. */
  ts: number;
}

export interface CrashHandlerOptions {
  /** Appelé pour chaque crash distinct (après dédup + filtrage de burst). */
  onCrash: (info: CrashInfo) => void;
  /** Cible des listeners (par défaut `window`). Utile pour tests. */
  target?: { addEventListener: typeof window.addEventListener; removeEventListener: typeof window.removeEventListener };
}

interface InstalledHandle {
  uninstall: () => void;
  /** Pour les tests : injection manuelle d'un crash. */
  _trigger: (kind: CrashKind, raw: { message: string; error?: unknown; reason?: unknown; filename?: string; lineno?: number }) => void;
}

export class CrashHandler {
  private static lastSignature: string | null = null;
  private static lastSignatureTs = 0;
  private static recentTimestamps: number[] = [];
  private static handlingDepth = 0;

  /**
   * Installe les listeners globaux. Retourne un handle qui permet de désinstaller
   * (utile pour les tests, ou si on veut hot-reload le handler en dev).
   */
  static install(opts: CrashHandlerOptions): InstalledHandle {
    const target = opts.target ?? window;

    const onError = (e: ErrorEvent) => {
      const info: CrashInfo = {
        kind: 'error',
        message: extractMessage(e.error) ?? e.message ?? 'Erreur inconnue',
        stack: extractStack(e.error),
        source: e.filename ? `${shortFile(e.filename)}:${e.lineno ?? '?'}` : undefined,
        ts: Date.now(),
      };
      CrashHandler.dispatch(info, opts.onCrash);
    };

    const onRejection = (e: PromiseRejectionEvent) => {
      const info: CrashInfo = {
        kind: 'rejection',
        message: extractMessage(e.reason) ?? 'Promesse rejetée',
        stack: extractStack(e.reason),
        ts: Date.now(),
      };
      CrashHandler.dispatch(info, opts.onCrash);
    };

    target.addEventListener('error', onError as EventListener);
    target.addEventListener('unhandledrejection', onRejection as EventListener);

    return {
      uninstall: () => {
        target.removeEventListener('error', onError as EventListener);
        target.removeEventListener('unhandledrejection', onRejection as EventListener);
      },
      _trigger: (kind, raw) => {
        if (kind === 'error') {
          const evt = { message: raw.message, error: raw.error, filename: raw.filename, lineno: raw.lineno } as unknown as ErrorEvent;
          onError(evt);
        } else {
          const evt = { reason: raw.reason } as unknown as PromiseRejectionEvent;
          onRejection(evt);
        }
      },
    };
  }

  /** Réinitialise l'état interne (entre les tests). */
  static reset(): void {
    CrashHandler.lastSignature = null;
    CrashHandler.lastSignatureTs = 0;
    CrashHandler.recentTimestamps = [];
    CrashHandler.handlingDepth = 0;
  }

  private static dispatch(info: CrashInfo, onCrash: (info: CrashInfo) => void): void {
    // Réentrance : si le callback déclenche lui-même un error event, on ignore
    // pour éviter une boucle infinie (sinon l'overlay qui crashe → re-overlay → …).
    if (CrashHandler.handlingDepth > 0) return;

    // Dédup : même signature en < 2s = ignoré.
    const sig = signature(info);
    if (sig === CrashHandler.lastSignature && info.ts - CrashHandler.lastSignatureTs < DEDUPE_WINDOW_MS) {
      return;
    }
    CrashHandler.lastSignature = sig;
    CrashHandler.lastSignatureTs = info.ts;

    // Garde-fou de burst : si > MAX_BURST crashes en BURST_WINDOW_MS, on stoppe.
    CrashHandler.recentTimestamps.push(info.ts);
    const cutoff = info.ts - BURST_WINDOW_MS;
    while (CrashHandler.recentTimestamps.length && CrashHandler.recentTimestamps[0] < cutoff) {
      CrashHandler.recentTimestamps.shift();
    }
    if (CrashHandler.recentTimestamps.length > MAX_BURST) return;

    CrashHandler.handlingDepth++;
    try {
      onCrash(info);
    } catch (cbErr) {
      // Le callback lui-même a échoué : on log mais on ne re-dispatch pas.
      // eslint-disable-next-line no-console
      console.error('[CrashHandler] callback onCrash a échoué :', cbErr);
    } finally {
      CrashHandler.handlingDepth--;
    }
  }
}

// === Helpers ===

function extractMessage(raw: unknown): string | undefined {
  if (raw == null) return undefined;
  if (raw instanceof Error) return raw.message || raw.name || 'Error';
  if (typeof raw === 'string') return raw;
  if (typeof raw === 'object' && 'message' in (raw as object)) {
    const m = (raw as { message?: unknown }).message;
    if (typeof m === 'string') return m;
  }
  try { return String(raw); } catch { return undefined; }
}

function extractStack(raw: unknown): string | undefined {
  if (raw instanceof Error && typeof raw.stack === 'string') return raw.stack;
  if (typeof raw === 'object' && raw !== null && 'stack' in (raw as object)) {
    const s = (raw as { stack?: unknown }).stack;
    if (typeof s === 'string') return s;
  }
  return undefined;
}

function shortFile(filename: string): string {
  // Garde le dernier segment de l'URL (sans la query)
  try {
    const noQuery = filename.split('?')[0];
    const seg = noQuery.split('/').pop();
    return seg || filename;
  } catch {
    return filename;
  }
}

function signature(info: CrashInfo): string {
  const stackLine = info.stack?.split('\n').slice(0, 2).join('|') ?? '';
  return `${info.kind}|${info.message}|${stackLine}`.slice(0, SIGNATURE_LEN);
}

/**
 * Format texte court d'un crash, prêt à être copié ou inclus dans un bug report.
 * Stack tronquée à ~12 lignes pour ne pas saturer un textarea.
 */
export function formatCrashForBugReport(info: CrashInfo): string {
  const lines: string[] = [];
  lines.push(`💥 Crash automatique (${info.kind === 'rejection' ? 'Promise rejection' : 'erreur JS'})`);
  lines.push(`Message : ${info.message}`);
  if (info.source) lines.push(`Source : ${info.source}`);
  if (info.stack) {
    const stackLines = info.stack.split('\n').slice(0, 12).join('\n');
    lines.push('');
    lines.push('Stack :');
    lines.push(stackLines);
  }
  return lines.join('\n');
}
